import type { PixelRows } from './pixelType'

/**
 * A lowercase stitch script on the plates' own grid, for words the plates
 * never spelled — the filter names on the gallery wall.
 *
 * Their lettering is 4px blocks, a five-block x-height, ascenders to nine
 * blocks, strokes one block wide, letters joined along the baseline (measured
 * off the "natural" plaque). Their letters also share strokes with each other,
 * which is why they cannot be cut apart and re-spelled: these are redrawn on
 * that grid instead, so any name an operator types in the Studio can be set.
 *
 * Rows 0–3 are the ascender zone, 4–8 the x-height (8 is the baseline), 9–11
 * the descender.
 */
const G: Record<string, PixelRows> = {
  a: ['....', '....', '....', '....', '.##.', '#..#', '#..#', '#.##', '.#.#'],
  b: ['#...', '#...', '#...', '#...', '###.', '#..#', '#..#', '#..#', '###.'],
  c: ['...', '...', '...', '...', '.##', '#..', '#..', '#..', '.##'],
  d: ['...#', '...#', '...#', '...#', '.###', '#..#', '#..#', '#..#', '.###'],
  e: ['....', '....', '....', '....', '.##.', '#..#', '####', '#...', '.###'],
  f: ['..##', '.#..', '.#..', '.#..', '###.', '.#..', '.#..', '.#..', '#...'],
  g: [
    '....',
    '....',
    '....',
    '....',
    '.###',
    '#..#',
    '#..#',
    '#..#',
    '.###',
    '...#',
    '...#',
    '.##.'
  ],
  h: ['#...', '#...', '#...', '#...', '#.#.', '##.#', '#..#', '#..#', '#..#'],
  i: ['.', '.', '.', '#', '.', '#', '#', '#', '#'],
  j: ['..', '..', '..', '.#', '..', '.#', '.#', '.#', '.#', '.#', '.#', '#.'],
  k: ['#...', '#...', '#...', '#...', '#..#', '#.#.', '##..', '#.#.', '#..#'],
  l: ['.#.', '#.#', '#.#', '##.', '#..', '#..', '#..', '#..', '.##'],
  m: ['.....', '.....', '.....', '.....', '##.#.', '#.#.#', '#.#.#', '#.#.#', '#.#.#'],
  n: ['....', '....', '....', '....', '#.#.', '##.#', '#..#', '#..#', '#..#'],
  o: ['....', '....', '....', '....', '.##.', '#..#', '#..#', '#..#', '.##.'],
  p: [
    '....',
    '....',
    '....',
    '....',
    '###.',
    '#..#',
    '#..#',
    '#..#',
    '###.',
    '#...',
    '#...',
    '#...'
  ],
  q: [
    '....',
    '....',
    '....',
    '....',
    '.###',
    '#..#',
    '#..#',
    '#..#',
    '.###',
    '...#',
    '...#',
    '...#'
  ],
  r: ['...', '...', '...', '...', '#.#', '##.', '#..', '#..', '#..'],
  s: ['...', '...', '...', '...', '.##', '#..', '.#.', '..#', '##.'],
  t: ['.#.', '.#.', '.#.', '###', '.#.', '.#.', '.#.', '.#.', '..#'],
  u: ['....', '....', '....', '....', '#..#', '#..#', '#..#', '#..#', '.###'],
  v: ['.....', '.....', '.....', '.....', '#...#', '#...#', '.#.#.', '.#.#.', '..#..'],
  w: ['.....', '.....', '.....', '.....', '#...#', '#...#', '#.#.#', '#.#.#', '.#.#.'],
  x: ['...', '...', '...', '...', '#.#', '#.#', '.#.', '#.#', '#.#'],
  y: [
    '....',
    '....',
    '....',
    '....',
    '#..#',
    '#..#',
    '#..#',
    '#..#',
    '.###',
    '...#',
    '...#',
    '.##.'
  ],
  z: ['....', '....', '....', '....', '####', '...#', '..#.', '.#..', '####'],
  '&': ['.....', '.....', '.##..', '#..#.', '.##..', '.#..#', '#.##.', '#..#.', '.##.#'],
  ' ': ['..', '..', '..', '..', '..', '..', '..', '..', '..']
}

const DESCENDERS = new Set(['g', 'j', 'p', 'q', 'y'])

/**
 * Set a word. Lowercased; characters the script has no letter for are left
 * out. Letters join with one block on the baseline where both meet it, as in
 * their hand — elsewhere a join would float as a stray dot.
 */
export function scriptWord(text: string): PixelRows {
  const letters = [...text.toLowerCase()].filter((c) => c in G)
  if (letters.length === 0) return ['']
  const rows = letters.some((c) => DESCENDERS.has(c)) ? 12 : 9
  const glyphs = letters.map((c) => {
    const g = G[c]
    const blank = '.'.repeat(g[0].length)
    return Array.from({ length: rows }, (_, r) => g[r] ?? blank)
  })
  return Array.from({ length: rows }, (_, r) =>
    glyphs
      .map((g, i) => {
        if (i === glyphs.length - 1) return g[r]
        const next = glyphs[i + 1]
        const joins = r === 8 && g[8].endsWith('#') && next[8].startsWith('#')
        return g[r] + (joins ? '#' : '.')
      })
      .join('')
  )
}

/** A filter's name as the wall shows it: without a "BK " catalogue prefix. */
export const displayName = (name: string): string => name.replace(/^bk\s+/i, '').trim()
